import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Polls unread state (DMs + reply notifications) every 8s while authenticated.
 * - Fires a toast when a new DM arrives (with sender + preview)
 * - Fires a toast when someone replies to your post in a room
 * - Mirrors the combined total to the OS-level app badge (red circle) via the
 *   Badging API — so users see attention needed on the home-screen icon
 *
 * DM read state clears when the user opens /dm or /dm/:id.
 * Reply read state clears on the server automatically when the user loads that
 * room's messages (server-side auto-clear in GET /rooms/{slug}/messages).
 */
export function useUnreadDMs() {
  const { user } = useAuth();
  const loc = useLocation();
  const lastDmCountRef = useRef(0);
  const lastDmIdRef = useRef(null);
  const lastReplyIdRef = useRef(null);

  const setBadge = (count) => {
    try {
      if (count > 0 && "setAppBadge" in navigator) {
        navigator.setAppBadge(count).catch(() => {});
      } else if (count === 0 && "clearAppBadge" in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
    } catch { /* silent — Badging API not supported */ }
  };

  useEffect(() => {
    if (!user || user === false) return;

    let alive = true;

    const poll = async () => {
      try {
        const { data } = await api.get("/me/unread");
        if (!alive) return;

        const dmCount = data.dms || 0;
        const replyCount = data.replies || 0;
        const total = data.total || 0;

        // DM toast on tick-up
        if (dmCount > lastDmCountRef.current) {
          // Also fetch latest DM preview for the toast body
          try {
            const dm = await api.get("/dms/unread");
            const l = dm.data.latest;
            if (l && l.id !== lastDmIdRef.current) {
              const preview = (l.body || "").slice(0, 60);
              toast(`💌 @${l.from_username}`, {
                description: preview || "(attachment)",
                style: {
                  background: "var(--nw-bg)",
                  border: "2px solid #00E5FF",
                  color: "var(--nw-text)",
                  borderRadius: 0,
                  fontFamily: "JetBrains Mono, monospace",
                },
              });
              lastDmIdRef.current = l.id;
            }
          } catch { /* silent */ }
        }

        // Reply toast — new reply notification appears
        const latestReply = data.latest_reply;
        if (latestReply && latestReply.msg_id !== lastReplyIdRef.current) {
          if (lastReplyIdRef.current !== null) {
            // Skip toast on very first poll (avoids replaying old ones on load)
            const preview = (latestReply.body_preview || "").slice(0, 60);
            toast(`↩︎ @${latestReply.from_username} replied to you`, {
              description: preview,
              style: {
                background: "var(--nw-bg)",
                border: "2px solid #FF3BFF",
                color: "var(--nw-text)",
                borderRadius: 0,
                fontFamily: "JetBrains Mono, monospace",
              },
              action: {
                label: "View",
                onClick: () => { window.location.href = `/room/${latestReply.room_slug}#msg-${latestReply.msg_id}`; },
              },
            });
          }
          lastReplyIdRef.current = latestReply.msg_id;
        }

        lastDmCountRef.current = dmCount;
        window.dispatchEvent(new CustomEvent("nw:unread-dms", { detail: { count: dmCount } }));
        window.dispatchEvent(new CustomEvent("nw:unread-mentions", { detail: { count: data.mentions || 0 } }));
        window.dispatchEvent(new CustomEvent("nw:unread-total", { detail: { total, dms: dmCount, replies: replyCount, mentions: data.mentions || 0 } }));
        setBadge(total);
      } catch {
        /* silent — likely a transient network hiccup */
      }
    };

    poll();
    const t = setInterval(poll, 8000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  // Mark DMs read on entering the DM inbox or a specific thread.
  useEffect(() => {
    if (!user || user === false) return;
    if (loc.pathname === "/dm" || loc.pathname.startsWith("/dm/")) {
      api.post("/dms/read").then(() => {
        lastDmCountRef.current = 0;
        window.dispatchEvent(new CustomEvent("nw:unread-dms", { detail: { count: 0 } }));
      }).catch(() => { /* silent */ });
    }
    // Reply notifications auto-clear server-side when the user loads a room's
    // messages, so no explicit call here — next poll will pick up the drop.
  }, [loc.pathname, user]);
}
